// src/components/VulnerabilityIntelligenceView.js
// Unified view with 3 tabs: CVE Database, Trending CVEs, Recommended CVEs
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import { 
  Shield, Search, Plus, AlertTriangle, Code, Calendar, TrendingUp, 
  Upload, RefreshCw, Target, Flame, Filter, X, Github, ExternalLink, Star
} from 'lucide-react';
import AddVulnerabilityModal from './AddVulnerabilityModal';
import UpdateVulnerabilityModal from './UpdateVulnerabilityModal';
import BulkUploadVulnerabilityModal from './BulkUploadVulnerabilityModal';
import TrendingFromNewsView from './TrendingFromNewsView';

const VulnerabilityIntelligenceView = ({ isSuperAdmin, canEditIncidents }) => {
  const { getToken } = useAuth();
  const { activeCompany } = useTenant();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('database'); // 'database', 'trending', 'recommended'
  
  // Database tab state
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [dbError, setDbError] = useState(null);
  const [dbSearchTerm, setDbSearchTerm] = useState('');
  const [dbFilterSeverity, setDbFilterSeverity] = useState('all');
  
  // Trending tab state
  const [trendingCVEs, setTrendingCVEs] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingError, setTrendingError] = useState(null);
  
  // Recommended tab state
  const [recommendations, setRecommendations] = useState([]);
  const [recStatistics, setRecStatistics] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState(null);
  const [recRefreshing, setRecRefreshing] = useState(false);
  const [minConfidence, setMinConfidence] = useState(60);
  const [recFilterSeverity, setRecFilterSeverity] = useState('all');
  
  // Shared state
  const [selectedCVE, setSelectedCVE] = useState(null);

  // GitHub enrichment state (per-CVE, keyed by cve_id)
  const [githubData, setGithubData] = useState({});
  const [githubEnrichLoading, setGithubEnrichLoading] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // ============================================
  // DATABASE TAB - Fetch all vulnerabilities
  // ============================================
  const fetchVulnerabilities = async () => {
    try {
      setDbLoading(true);
      const token = await getToken();
      
      const params = new URLSearchParams({
        severity: dbFilterSeverity,
        search: dbSearchTerm,
        limit: 1000
      });

      const response = await fetch(`/api/vulnerabilities?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch vulnerabilities');

      const data = await response.json();
      setVulnerabilities(data.vulnerabilities || []);
      setDbError(null);
      console.log(`✅ Loaded ${data.vulnerabilities.length} vulnerabilities`);
    } catch (err) {
      console.error('❌ Error fetching vulnerabilities:', err);
      setDbError(err.message);
    } finally {
      setDbLoading(false);
    }
  };

  // ============================================
  // TRENDING TAB - Fetch trending CVEs
  // ============================================
  const fetchTrendingCVEs = async () => {
    try {
      setTrendingLoading(true);
      const token = await getToken();

      const response = await fetch('/api/vulnerabilities/trending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch trending CVEs');

      const data = await response.json();
      setTrendingCVEs(data.trending || []);
      setTrendingError(null);
      console.log(`✅ Loaded ${data.trending.length} trending CVEs`);
    } catch (err) {
      console.error('❌ Error fetching trending CVEs:', err);
      setTrendingError(err.message);
    } finally {
      setTrendingLoading(false);
    }
  };

  // ============================================
  // RECOMMENDED TAB - Fetch company-specific recommendations
  // ============================================
  const fetchRecommendations = async (refresh = false) => {
    try {
      setRecLoading(true);
      const token = await getToken();
      
      if (!activeCompany?.numeric_id) {
        throw new Error('No active company selected');
      }

      const params = new URLSearchParams({
        minConfidence,
        severity: recFilterSeverity,
        limit: 500,
        refresh: refresh.toString()
      });

      const response = await fetch(
        `/api/companies/${activeCompany.numeric_id}/recommended-cves?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch recommendations');

      const data = await response.json();
      setRecommendations(data.recommendations || []);
      setRecStatistics(data.statistics || null);
      setRecError(null);
      console.log(`✅ Loaded ${data.recommendations.length} recommended CVEs`);
    } catch (err) {
      console.error('❌ Error fetching recommendations:', err);
      setRecError(err.message);
    } finally {
      setRecLoading(false);
      setRecRefreshing(false);
    }
  };

  const handleRefreshRecommendations = async () => {
    setRecRefreshing(true);
    await fetchRecommendations(true);
  };

  // ============================================
  // GITHUB ENRICHMENT
  // ============================================
  const fetchGithubData = async (cveId) => {
    if (!cveId) return;
    const existing = githubData[cveId];
    if (existing?.loading) return;
    if (existing?.enriched_at) {
      const ageMs = Date.now() - new Date(existing.enriched_at).getTime();
      if (ageMs < 60 * 60 * 1000) return; // cached < 1hr
    }
    setGithubData(prev => ({ ...prev, [cveId]: { ...prev[cveId], loading: true, error: null } }));
    try {
      const token = await getToken();
      const response = await fetch(`/api/vulnerabilities/${cveId}/github`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const err = await response.json();
        if (err.setup_required) {
          setGithubData(prev => ({ ...prev, [cveId]: { loading: false, error: 'not_configured' } }));
          return;
        }
        throw new Error(err.error || 'Failed to fetch GitHub data');
      }
      const data = await response.json();
      setGithubData(prev => ({ ...prev, [cveId]: { ...data, loading: false, error: null } }));
    } catch (err) {
      setGithubData(prev => ({ ...prev, [cveId]: { loading: false, error: err.message } }));
    }
  };

  const handleGithubBatchEnrich = async () => {
    try {
      setGithubEnrichLoading(true);
      const token = await getToken();
      const response = await fetch('/api/vulnerabilities/github/enrich-batch', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 30 }),
      });
      const data = await response.json();
      console.log('[GitHub] Batch enrich started:', data.message);
      setTimeout(() => fetchVulnerabilities(), 5000);
    } catch (err) {
      console.error('[GitHub] Batch enrich error:', err);
    } finally {
      setGithubEnrichLoading(false);
    }
  };

  // ============================================
  // Effects - Load data when tab changes
  // ============================================
  useEffect(() => {
    if (activeTab === 'database') {
      fetchVulnerabilities();
    } else if (activeTab === 'trending') {
      if (trendingCVEs.length === 0) fetchTrendingCVEs();
    } else if (activeTab === 'recommended') {
      if (activeCompany?.numeric_id) fetchRecommendations();
    }
  }, [activeTab, activeCompany?.numeric_id]);

  // Database filters effect
  useEffect(() => {
    if (activeTab === 'database') {
      fetchVulnerabilities();
    }
  }, [dbFilterSeverity, dbSearchTerm]);

  // Auto-fetch GitHub data when a CVE is selected on database tab
  useEffect(() => {
    if (activeTab === 'database' && selectedCVE?.cve_id) {
      fetchGithubData(selectedCVE.cve_id);
    }
  }, [selectedCVE?.cve_id, activeTab]);

  // Recommended filters effect
  useEffect(() => {
    if (activeTab === 'recommended' && activeCompany?.numeric_id && !recLoading) {
      fetchRecommendations();
    }
  }, [minConfidence, recFilterSeverity]);

  // ============================================
  // Utility functions
  // ============================================
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'High': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'Low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getCVSSColor = (score) => {
    if (score >= 9.0) return 'text-red-400';
    if (score >= 7.0) return 'text-orange-400';
    if (score >= 4.0) return 'text-yellow-400';
    return 'text-blue-400';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-green-400 bg-green-500/20 border-green-500/30';
    if (confidence >= 70) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
  };

  const handleVulnerabilityAdded = (newVulnerability) => {
    setVulnerabilities(prev => [newVulnerability, ...prev]);
    setSelectedCVE(newVulnerability);
  };

  const handleVulnerabilityUpdated = (updatedVulnerability) => {
    setVulnerabilities(prev => 
      prev.map(v => v.id === updatedVulnerability.id ? updatedVulnerability : v)
    );
    setSelectedCVE(updatedVulnerability);
  };

  const handleBulkUploadComplete = () => {
    fetchVulnerabilities();
  };

  // Get current data based on active tab
  const getCurrentData = () => {
    if (activeTab === 'database') return vulnerabilities;
    if (activeTab === 'trending') return trendingCVEs;
    if (activeTab === 'recommended') return recommendations;
    return [];
  };

  const getCurrentLoading = () => {
    if (activeTab === 'database') return dbLoading;
    if (activeTab === 'trending') return trendingLoading;
    if (activeTab === 'recommended') return recLoading;
    return false;
  };

  const getCurrentError = () => {
    if (activeTab === 'database') return dbError;
    if (activeTab === 'trending') return trendingError;
    if (activeTab === 'recommended') return recError;
    return null;
  };

  const currentData = getCurrentData();
  const loading = getCurrentLoading();
  const error = getCurrentError();

 if (loading && currentData.length === 0 && activeTab !== 'trending') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 text-cyan-400 animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }
 
  // ── Trending tab uses its own full-width layout (cards + table) ──────────
  if (activeTab === 'trending') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 min-h-0">
          <TrendingFromNewsView
            onTabChange={setActiveTab}
            isSuperAdmin={isSuperAdmin}
          />
        </div>
      </div>
    );
  }
 
  return (
    <>
      <div className="h-full flex">
        {/* LEFT SIDEBAR */}
        <div className="w-[420px] border-r border-slate-800/50 bg-slate-900/30 flex flex-col">
          <div className="px-4 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                Vulnerabilities
              </h2>
              {isSuperAdmin && activeTab === 'database' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleGithubBatchEnrich}
                    disabled={githubEnrichLoading}
                    className="px-2 py-1.5 bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/50 rounded-lg text-xs text-slate-300 font-medium transition-all disabled:opacity-50 flex items-center gap-1"
                    title="Enrich CVEs with GitHub repo mention counts"
                  >
                    <Github className={`w-4 h-4 ${githubEnrichLoading ? 'text-green-400' : 'text-slate-400'}`} />
                    <span>{githubEnrichLoading ? 'Enriching...' : 'GitHub'}</span>
                  </button>
                  <button
                    onClick={() => setShowBulkUploadModal(true)}
                    className="px-2 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-xs text-purple-300 font-medium transition-all"
                    title="Bulk Upload"
                  >
                    <Upload className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs text-white font-medium transition-all"
                  >
                    <Plus className="w-4 h-4 inline-block mr-1" />
                    Add CVE
                  </button>
                </div>
              )}
              {activeTab === 'recommended' && (
                <button
                  onClick={handleRefreshRecommendations}
                  disabled={recRefreshing}
                  className="px-2 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-xs text-cyan-300 font-medium transition-all disabled:opacity-50"
                  title="Refresh Recommendations"
                >
                  <RefreshCw className={`w-4 h-4 ${recRefreshing ? 'animate-spin' : ''}`} />
                </button>
              )}
            </div>

            {/* TAB SELECTOR - 3 TABS */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setActiveTab('database')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'database'
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                    : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800/70'
                }`}
              >
                <Shield className="w-4 h-4 inline-block mr-1" />
                CVEs
              </button>
              
              <button
                onClick={() => setActiveTab('trending')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'trending'
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                    : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800/70'
                }`}
              >
                <Flame className="w-4 h-4 inline-block mr-1" />
                Trending
              </button>
              
              <button
                onClick={() => setActiveTab('recommended')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'recommended'
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800/70'
                }`}
              >
                <Target className="w-4 h-4 inline-block mr-1" />
                For You
              </button>
            </div>

            {/* TAB-SPECIFIC FILTERS */}
            {activeTab === 'database' && (
              <>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search CVEs..."
                    value={dbSearchTerm}
                    onChange={(e) => setDbSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
                  />
                </div>

                <select
                  value={dbFilterSeverity}
                  onChange={(e) => setDbFilterSeverity(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                >
                  <option value="all">All Severities</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                  <option value="Informational">Informational</option>
                </select>
              </>
            )}

            {activeTab === 'recommended' && (
              <>
                {/* Statistics Card */}
                {recStatistics && (
                  <div className="mb-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-slate-500">Total:</span>
                        <span className="ml-2 text-slate-200 font-semibold">
                          {recStatistics.unique_cves}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Critical:</span>
                        <span className="ml-2 text-red-400 font-semibold">
                          {recStatistics.critical_count}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Exploits:</span>
                        <span className="ml-2 text-orange-400 font-semibold">
                          {recStatistics.exploit_available_count}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">CISA KEV:</span>
                        <span className="ml-2 text-purple-400 font-semibold">
                          {recStatistics.cisa_kev_count || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Confidence Slider */}
                <div className="mb-3">
                  <label className="block text-xs text-slate-400 mb-1">
                    Minimum Confidence: {minConfidence}%
                  </label>
                  <input
                    type="range"
                    min="40"
                    max="100"
                    step="10"
                    value={minConfidence}
                    onChange={(e) => setMinConfidence(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <select
                  value={recFilterSeverity}
                  onChange={(e) => setRecFilterSeverity(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-cyan-500/50"
                >
                  <option value="all">All Severities</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </>
            )}

            <div className="mt-3 text-xs text-slate-400">
              {activeTab === 'database' && `${vulnerabilities.length} vulnerabilities tracked`}
              {activeTab === 'trending' && `${trendingCVEs.length} trending CVEs`}
              {activeTab === 'recommended' && `${recommendations.length} recommendations for your tech stack`}
            </div>
          </div>

          {/* CVE LIST */}
          <div className="flex-1 overflow-y-auto">
            {error && (
              <div className="m-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
            
            {currentData.length === 0 && !error && (
              <div className="p-8 text-center text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  {activeTab === 'recommended' && 'No recommendations found'}
                  {activeTab === 'trending' && 'No trending CVEs at this time'}
                  {activeTab === 'database' && 'No vulnerabilities found'}
                </p>
                {activeTab === 'recommended' && (
                  <p className="text-xs text-slate-600 mt-2">
                    Try lowering the confidence threshold or click refresh
                  </p>
                )}
              </div>
            )}

            {currentData.map((cve) => (
              <div
                key={activeTab === 'recommended' ? cve.recommendation_id : cve.id}
                onClick={() => setSelectedCVE(cve)}
                className={`p-4 border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition-all ${
                  selectedCVE?.cve_id === cve.cve_id ? 'bg-slate-800/50 border-l-4 border-l-cyan-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-semibold text-cyan-400 text-sm">
                        {cve.cve_id}
                      </span>
                      
                      {/* Recommended tab - show confidence */}
                      {activeTab === 'recommended' && cve.match_confidence && (
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium border ${getConfidenceColor(cve.match_confidence)}`}>
                          {cve.match_confidence}%
                        </span>
                      )}

                      {/* Trending tab - show trending score */}
                      {activeTab === 'trending' && cve.trending_score && (
                        <span className="px-1.5 py-0.5 bg-orange-500/20 border border-orange-500/30 rounded text-xs text-orange-300 font-medium flex items-center gap-1">
                          <Flame className="w-3 h-3" />
                          {cve.trending_score}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-slate-300 line-clamp-2 mb-2">
                      {cve.title}
                    </p>

                    {/* Recommended tab - show technology */}
                    {activeTab === 'recommended' && cve.technology_name && (
                      <div className="flex items-center gap-2 text-xs mb-2">
                        <Code className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400">{cve.technology_name}</span>
                        {cve.tech_version && (
                          <span className="text-slate-500">v{cve.tech_version}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    {cve.severity && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getSeverityColor(cve.severity)}`}>
                        {cve.severity}
                      </span>
                    )}
                    {cve.cvss_score && (
                      <span className={`text-xs font-semibold ${getCVSSColor(cve.cvss_score)}`}>
                        {cve.cvss_score}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {cve.exploit_available && (
                    <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/30 rounded text-xs text-red-300">
                      🔥 Exploit
                    </span>
                  )}
                  {cve.in_the_wild && (
                    <span className="px-2 py-0.5 bg-orange-500/20 border border-orange-500/30 rounded text-xs text-orange-300">
                      ⚠️ In Wild
                    </span>
                  )}
                  {cve.patch_available && (
                    <span className="px-2 py-0.5 bg-green-500/20 border border-green-500/30 rounded text-xs text-green-300">
                      ✓ Patch
                    </span>
                  )}
                  {cve.epss_score != null && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                      cve.epss_percentile >= 0.9
                        ? 'bg-red-500/20 border-red-500/40 text-red-300'
                        : cve.epss_percentile >= 0.7
                        ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                        : 'bg-slate-700/40 border-slate-600/40 text-slate-400'
                    }`}>
                      EPSS {(cve.epss_score * 100).toFixed(1)}%
                    </span>
                  )}
                  {cve.published_date && (
                    <span className="text-xs text-slate-500">
                      {new Date(cve.published_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT PANEL - DETAILS */}
        <div className="flex-1 overflow-y-auto">
          {selectedCVE ? (
            <>
              <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800/50 px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-100 mb-1 flex items-center gap-3 flex-wrap">
                      {selectedCVE.cve_id}
                      <span className={`px-3 py-1 rounded-lg text-sm font-medium border ${getSeverityColor(selectedCVE.severity)}`}>
                        {selectedCVE.severity}
                      </span>
                      {selectedCVE.cvss_score && (
                        <span className={`text-lg font-bold ${getCVSSColor(selectedCVE.cvss_score)}`}>
                          CVSS {selectedCVE.cvss_score}
                        </span>
                      )}
                      {selectedCVE.epss_score != null && (
                        <span className={`px-2 py-1 rounded-lg text-sm font-semibold border ${
                          selectedCVE.epss_percentile >= 0.9
                            ? 'bg-red-500/20 border-red-500/40 text-red-300'
                            : selectedCVE.epss_percentile >= 0.7
                            ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                            : 'bg-slate-700/40 border-slate-600/40 text-slate-400'
                        }`}>
                          EPSS {(selectedCVE.epss_score * 100).toFixed(2)}%
                          {selectedCVE.epss_percentile != null && (
                            <span className="ml-1 text-xs opacity-70">
                              ({Math.round(selectedCVE.epss_percentile * 100)}th pct)
                            </span>
                          )}
                        </span>
                      )}
                    </h3>
                    {selectedCVE.title && (
                      <p className="text-slate-300">{selectedCVE.title}</p>
                    )}
                  </div>
                  {isSuperAdmin && activeTab === 'database' && (
                    <button
                      onClick={() => setShowUpdateModal(true)}
                      className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-sm text-white font-medium transition-all"
                    >
                      Edit CVE
                    </button>
                  )}
                </div>

                {/* Match Info Card - Only for Recommended tab */}
                {activeTab === 'recommended' && selectedCVE.match_confidence && (
                  <div className="mt-4 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Target className="w-5 h-5 text-cyan-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-cyan-300">
                            Match Confidence: {selectedCVE.match_confidence}%
                          </span>
                          <span className="px-2 py-0.5 bg-cyan-500/20 rounded text-xs text-cyan-300">
                            {selectedCVE.match_type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-cyan-200/80">
                          {selectedCVE.match_reason}
                        </p>
                        {selectedCVE.technology_name && (
                          <div className="mt-2 flex items-center gap-2">
                            <Code className="w-4 h-4 text-cyan-400" />
                            <span className="text-sm text-cyan-300">
                              Affects: <strong>{selectedCVE.technology_name}</strong>
                              {selectedCVE.tech_version && ` (v${selectedCVE.tech_version})`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="max-w-4xl space-y-6">
                  {/* Overview */}
                  <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-cyan-400" />
                      Overview
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      {selectedCVE.published_date && (
                        <div>
                          <span className="text-slate-500">Published:</span>
                          <span className="ml-2 text-slate-200">
                            {new Date(selectedCVE.published_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {selectedCVE.last_modified && (
                        <div>
                          <span className="text-slate-500">Last Modified:</span>
                          <span className="ml-2 text-slate-200">
                            {new Date(selectedCVE.last_modified).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {selectedCVE.vendor && (
                        <div>
                          <span className="text-slate-500">Vendor:</span>
                          <span className="ml-2 text-slate-200">{selectedCVE.vendor}</span>
                        </div>
                      )}
                      {selectedCVE.cwe && (
                        <div>
                          <span className="text-slate-500">CWE:</span>
                          <span className="ml-2 text-slate-200">{selectedCVE.cwe}</span>
                        </div>
                      )}
                    </div>
                    {selectedCVE.description && (
                      <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
                        <p className="text-sm text-slate-300 leading-relaxed">{selectedCVE.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Affected Products */}
                  {selectedCVE.affected_products && selectedCVE.affected_products.length > 0 && (
                    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                      <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                        <Code className="w-5 h-5 text-cyan-400" />
                        Affected Products
                      </h3>
                      <div className="space-y-2">
                        {selectedCVE.affected_products.map((product, idx) => (
                          <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                            <span className="text-sm text-slate-200 font-mono">{product}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CPEs */}
                  {selectedCVE.cpes && selectedCVE.cpes.length > 0 && (
                    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                      <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                        <Code className="w-5 h-5 text-cyan-400" />
                        CPE Identifiers
                      </h3>
                      <div className="space-y-2">
                        {selectedCVE.cpes.map((cpe, idx) => (
                          <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                            <span className="text-sm text-slate-200 font-mono break-all">{cpe}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status Indicators */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className={`p-4 rounded-lg border ${selectedCVE.exploit_available ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/30 border-slate-700/50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`w-5 h-5 ${selectedCVE.exploit_available ? 'text-red-400' : 'text-slate-500'}`} />
                        <span className="font-semibold text-slate-200">Exploit Status</span>
                      </div>
                      <p className={`text-sm ${selectedCVE.exploit_available ? 'text-red-300' : 'text-slate-400'}`}>
                        {selectedCVE.exploit_available ? 'Public Exploit Available' : 'No Known Exploits'}
                      </p>
                      {selectedCVE.exploit_source && (
                        <p className="text-xs text-slate-500 mt-1">Source: {selectedCVE.exploit_source}</p>
                      )}
                    </div>
                    <div className={`p-4 rounded-lg border ${selectedCVE.patch_available ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-800/30 border-slate-700/50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className={`w-5 h-5 ${selectedCVE.patch_available ? 'text-green-400' : 'text-slate-500'}`} />
                        <span className="font-semibold text-slate-200">Patch Status</span>
                      </div>
                      <p className={`text-sm ${selectedCVE.patch_available ? 'text-green-300' : 'text-slate-400'}`}>
                        {selectedCVE.patch_available ? 'Patch Available' : 'No Patch Available'}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg border ${selectedCVE.in_the_wild ? 'bg-orange-500/10 border-orange-500/30' : 'bg-slate-800/30 border-slate-700/50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`w-5 h-5 ${selectedCVE.in_the_wild ? 'text-orange-400' : 'text-slate-500'}`} />
                        <span className="font-semibold text-slate-200">In The Wild</span>
                      </div>
                      <p className={`text-sm ${selectedCVE.in_the_wild ? 'text-orange-300' : 'text-slate-400'}`}>
                        {selectedCVE.in_the_wild ? 'Actively Exploited' : 'Not Exploited in Wild'}
                      </p>
                    </div>
                  </div>

                  {/* EPSS Score Detail Card */}
                  {selectedCVE.epss_score != null && (
                    <div className={`rounded-lg border p-5 ${
                      selectedCVE.epss_percentile >= 0.9 ? 'bg-red-500/5 border-red-500/30'
                      : selectedCVE.epss_percentile >= 0.7 ? 'bg-orange-500/5 border-orange-500/30'
                      : 'bg-slate-800/30 border-slate-700/50'
                    }`}>
                      <h3 className="text-base font-semibold text-slate-100 mb-3 flex items-center gap-2">
                        <TrendingUp className={`w-4 h-4 ${selectedCVE.epss_percentile >= 0.9 ? 'text-red-400' : selectedCVE.epss_percentile >= 0.7 ? 'text-orange-400' : 'text-slate-400'}`} />
                        EPSS — Exploit Prediction Scoring System
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                          <div className={`text-2xl font-bold ${selectedCVE.epss_percentile >= 0.9 ? 'text-red-400' : selectedCVE.epss_percentile >= 0.7 ? 'text-orange-400' : 'text-slate-300'}`}>
                            {(selectedCVE.epss_score * 100).toFixed(2)}%
                          </div>
                          <div className="text-xs text-slate-500 mt-1">Exploitation Probability</div>
                        </div>
                        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-cyan-400">{Math.round((selectedCVE.epss_percentile || 0) * 100)}th</div>
                          <div className="text-xs text-slate-500 mt-1">Percentile</div>
                        </div>
                        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                          <div className={`text-sm font-semibold mt-1 ${selectedCVE.epss_percentile >= 0.9 ? 'text-red-300' : selectedCVE.epss_percentile >= 0.7 ? 'text-orange-300' : selectedCVE.epss_percentile >= 0.5 ? 'text-yellow-300' : 'text-slate-400'}`}>
                            {selectedCVE.epss_percentile >= 0.9 ? '🔴 Very High Risk' : selectedCVE.epss_percentile >= 0.7 ? '🟠 High Risk' : selectedCVE.epss_percentile >= 0.5 ? '🟡 Moderate Risk' : '🟢 Lower Risk'}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">Risk Category</div>
                          {selectedCVE.epss_last_checked && (
                            <div className="text-xs text-slate-600 mt-1">Updated {new Date(selectedCVE.epss_last_checked).toLocaleDateString()}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Exploit References */}
                  {selectedCVE.exploit_references && selectedCVE.exploit_references.length > 0 && (
                    <div className="bg-red-500/5 rounded-lg border border-red-500/20 p-5">
                      <h3 className="text-base font-semibold text-slate-100 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        Known Exploits ({selectedCVE.exploit_references.length})
                      </h3>
                      <div className="space-y-2">
                        {selectedCVE.exploit_references.map((ref, idx) => (
                          <a key={idx} href={ref.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-start gap-3 p-3 bg-slate-900/60 rounded-lg border border-red-500/20 hover:border-red-500/40 transition-colors group">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-red-300 group-hover:text-red-200 font-medium truncate">{ref.title}</p>
                              <div className="flex items-center gap-3 mt-1">
                                {ref.type && ref.type !== 'Unknown' && (
                                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded border border-red-500/20">{ref.type}</span>
                                )}
                                {ref.source && <span className="text-xs text-slate-500">{ref.source}</span>}
                                {ref.published && <span className="text-xs text-slate-600">{new Date(ref.published).toLocaleDateString()}</span>}
                              </div>
                            </div>
                            <span className="text-xs text-slate-600 group-hover:text-cyan-400 transition-colors mt-0.5 shrink-0">↗</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Remediation */}
                  {selectedCVE.workaround && (
                    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                      <h3 className="text-lg font-semibold text-slate-100 mb-4">
                        Remediation
                      </h3>
                      <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <p className="text-sm text-blue-200">{selectedCVE.workaround}</p>
                      </div>
                    </div>
                  )}

                  {/* References */}
                  {selectedCVE.reference_urls && selectedCVE.reference_urls.length > 0 && (
                    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                      <h3 className="text-lg font-semibold text-slate-100 mb-4">
                        References
                      </h3>
                      <div className="space-y-2">
                        {selectedCVE.reference_urls.map((ref, idx) => (
                          <a
                            key={idx}
                            href={ref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 hover:border-cyan-500/30 transition-colors"
                          >
                            <span className="text-sm text-cyan-400 hover:text-cyan-300 break-all">{ref}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* GitHub Mentions */}
                  {(() => {
                    const gh = githubData[selectedCVE.cve_id];
                    const repoCount = gh?.total_count ?? selectedCVE.github_repo_count;
                    return (
                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                            <Github className="w-5 h-5 text-slate-300" />
                            GitHub Mentions
                          </h3>
                          <button
                            onClick={() => fetchGithubData(selectedCVE.cve_id)}
                            disabled={gh?.loading}
                            className="px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700/70 border border-slate-600/50 rounded-lg text-xs text-slate-300 transition-all disabled:opacity-50 flex items-center gap-1.5"
                          >
                            <RefreshCw className={`w-3 h-3 ${gh?.loading ? 'animate-spin text-cyan-400' : ''}`} />
                            {gh?.loading ? 'Searching...' : 'Refresh'}
                          </button>
                        </div>

                        {gh?.error === 'not_configured' ? (
                          <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                            <p className="text-sm text-yellow-300">GITHUB_API_KEY not configured. Add it to .env to enable GitHub enrichment.</p>
                          </div>
                        ) : gh?.loading && repoCount == null ? (
                          <div className="flex items-center gap-3 p-4 text-slate-400">
                            <RefreshCw className="w-5 h-5 animate-spin text-cyan-400" />
                            <span className="text-sm">Searching GitHub repositories...</span>
                          </div>
                        ) : repoCount != null ? (
                          <>
                            <div className="flex items-center gap-4 mb-4 p-4 bg-slate-900/50 rounded-lg">
                              <div className="text-center">
                                <div className={`text-3xl font-bold ${
                                  repoCount > 100 ? 'text-green-400' :
                                  repoCount > 20  ? 'text-blue-400' :
                                  repoCount > 5   ? 'text-yellow-400' :
                                  'text-slate-400'
                                }`}>
                                  {repoCount > 999 ? '1000+' : repoCount}
                                </div>
                                <div className="text-xs text-slate-500 mt-1">repositories</div>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-slate-300">
                                  {repoCount === 0
                                    ? 'No public GitHub repositories reference this CVE.'
                                    : repoCount === 1
                                    ? '1 public repository mentions this CVE.'
                                    : `${repoCount.toLocaleString()} public repositories mention this CVE — indicating ${
                                        repoCount > 100 ? 'very high community interest and likely PoC/exploit activity'
                                        : repoCount > 20 ? 'significant researcher activity'
                                        : 'some community interest'
                                      }.`
                                  }
                                </p>
                                {gh?.enriched_at && (
                                  <p className="text-xs text-slate-500 mt-1">Updated {new Date(gh.enriched_at).toLocaleString()}</p>
                                )}
                              </div>
                            </div>
                            {gh?.top_repos && gh.top_repos.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Top Repositories</p>
                                {gh.top_repos.map((repo, idx) => (
                                  <a
                                    key={idx}
                                    href={repo.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 hover:border-slate-500/50 hover:bg-slate-800/50 transition-all group"
                                  >
                                    <div className="flex-1 min-w-0 mr-3">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-mono text-cyan-400 group-hover:text-cyan-300 truncate">{repo.name}</span>
                                        {repo.language && (
                                          <span className="text-xs px-1.5 py-0.5 bg-slate-700/50 rounded text-slate-400 shrink-0">{repo.language}</span>
                                        )}
                                      </div>
                                      {repo.description && (
                                        <p className="text-xs text-slate-400 line-clamp-1">{repo.description}</p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-slate-500 shrink-0">
                                      <Star className="w-3 h-3" />
                                      <span>{repo.stars?.toLocaleString() || 0}</span>
                                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  </a>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                            <p className="text-sm text-slate-400">Click Refresh to check GitHub for mentions of this CVE.</p>
                            <button
                              onClick={() => fetchGithubData(selectedCVE.cve_id)}
                              className="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-xs text-cyan-300 transition-all"
                            >
                              Search GitHub
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <Shield className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Select a CVE to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals - Only show for database tab */}
      {activeTab === 'database' && (
        <>
          <AddVulnerabilityModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onVulnerabilityAdded={handleVulnerabilityAdded}
          />

          <UpdateVulnerabilityModal
            isOpen={showUpdateModal}
            onClose={() => setShowUpdateModal(false)}
            vulnerability={selectedCVE}
            onVulnerabilityUpdated={handleVulnerabilityUpdated}
          />

          <BulkUploadVulnerabilityModal
            isOpen={showBulkUploadModal}
            onClose={() => setShowBulkUploadModal(false)}
            onUploadComplete={handleBulkUploadComplete}
          />
        </>
      )}
    </>
  );
};

export default VulnerabilityIntelligenceView;